import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  LayoutDashboard,
  Loader2,
  PhoneCall,
  Save,
  Settings as SettingsIcon,
  ShieldAlert,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components_dashboard/ui/select';
import { Switch } from '../components_dashboard/ui/switch';
import { Textarea } from '../components_dashboard/ui/textarea';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

const verdictBadge = (verdict) => {
  switch (verdict) {
    case 'SPAM':
      return 'bg-red-100 text-red-700 border border-red-200';
    case 'CLEAN_IDENTIFIED':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'NO_LABEL':
    case 'NO_OVERLAY':
    case 'UNVERIFIED':
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'ERROR':
      return 'bg-slate-200 text-slate-600 border border-slate-300';
    default:
      return 'bg-slate-100 text-slate-500 border border-slate-200';
  }
};

const verdictLabel = (verdict) => {
  switch (verdict) {
    case 'SPAM': return 'Spam';
    case 'CLEAN_IDENTIFIED': return 'Clean';
    case 'NO_LABEL': return 'No label';
    case 'NO_OVERLAY': return 'No overlay';
    case 'UNVERIFIED': return 'Unverified';
    case 'ERROR': return 'Error';
    default: return 'Not checked';
  }
};

// Backend stores timestamps in UTC. Display everything in IST (Asia/Kolkata)
// so "Last checked" matches the wall clock in India.
const formatIST = (value) => {
  if (!value) return '—';
  let text = String(value).trim().replace(' ', 'T');
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(text)) text += 'Z';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16).replace('T', ' ');
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const VIEW_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, hint: 'Pool stats and per-number spam verdicts.' },
  { key: 'settings', label: 'Settings', icon: SettingsIcon, hint: 'Scan targets, schedule, and app devices.' },
];

const emptyResultDraft = {
  app: 'truecaller',
  overlay_present: true,
  caller_label: '',
  is_spam: 'unknown',
  report_count: '',
  raw_text: '',
};

export default function SpamDetectionDashboard() {
  const { token } = useAuth();
  const [numbers, setNumbers] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [activeView, setActiveView] = useState('dashboard');

  const [settingsDraft, setSettingsDraft] = useState({ target_numbers: [], daily_times: [], schedule_enabled: true, auto_dial: true, call_window_minutes: 10 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [newNumber, setNewNumber] = useState('');

  const [devices, setDevices] = useState([]);
  const [deviceDraft, setDeviceDraft] = useState({ name: '', msisdn: '', network: '' });
  const [savingDevice, setSavingDevice] = useState(false);
  const [createdToken, setCreatedToken] = useState('');

  const [historyNumber, setHistoryNumber] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [resultCheck, setResultCheck] = useState(null);
  const [resultDraft, setResultDraft] = useState(emptyResultDraft);
  const [submittingResult, setSubmittingResult] = useState(false);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const fetchOverview = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/spam/overview`, authHeaders);
      const rows = Array.isArray(response.data?.numbers) ? response.data.numbers : [];
      setNumbers(rows);
      setStats(response.data?.stats || null);
      const remoteSettings = response.data?.settings || {};
      setSettings(remoteSettings);
      const remoteTargets = Array.isArray(remoteSettings.target_numbers) ? remoteSettings.target_numbers : [];
      setSettingsDraft({
        target_numbers: remoteTargets.length
          ? remoteTargets
          : (remoteSettings.test_phone_number ? [{ number: remoteSettings.test_phone_number, enabled: true }] : []),
        daily_times: Array.isArray(remoteSettings.daily_times) ? remoteSettings.daily_times : [],
        schedule_enabled: remoteSettings.schedule_enabled !== false,
        auto_dial: remoteSettings.auto_dial !== false,
        call_window_minutes: remoteSettings.call_window_minutes || 10,
      });
      setDevices(Array.isArray(response.data?.devices) ? response.data.devices : []);
    } catch (error) {
      console.error('Failed to fetch spam overview', error);
      toast.error(error.response?.data?.detail || 'Failed to load spam detection data.');
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const filteredNumbers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return numbers.filter((row) => {
      if (verdictFilter === 'spam' && row.spam_verdict !== 'SPAM') return false;
      if (verdictFilter === 'clean' && row.spam_verdict !== 'CLEAN_IDENTIFIED') return false;
      if (verdictFilter === 'unverified' && !['NO_LABEL', 'NO_OVERLAY', 'UNVERIFIED'].includes(row.spam_verdict)) return false;
      if (verdictFilter === 'unchecked' && row.spam_verdict) return false;
      if (verdictFilter === 'quarantined' && !row.quarantined) return false;
      if (!query) return true;
      return (
        String(row.number || '').toLowerCase().includes(query)
        || String(row.provider || '').toLowerCase().includes(query)
        || String(row.spam_label || '').toLowerCase().includes(query)
      );
    });
  }, [numbers, searchQuery, verdictFilter]);

  const spamRatePct = stats && stats.total ? Math.round((stats.spam_rate || 0) * 100) : 0;

  const handleRun = async (didIds) => {
    try {
      setRunning(true);
      const payload = didIds && didIds.length ? { number_ids: didIds } : { all: true };
      const response = await axios.post(`${apiUrl}/api/v1/admin/spam/run`, payload, authHeaders);
      const created = Array.isArray(response.data?.checks) ? response.data.checks : [];
      toast.success(`Queued ${created.length} spam check${created.length === 1 ? '' : 's'}.`);
      await fetchOverview();
      return created;
    } catch (error) {
      console.error('Failed to queue spam checks', error);
      toast.error(error.response?.data?.detail || 'Failed to queue spam checks.');
      return [];
    } finally {
      setRunning(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryNumber(row);
    setHistoryRows([]);
    try {
      setLoadingHistory(true);
      const response = await axios.get(
        `${apiUrl}/api/v1/admin/spam/checks?did_number_id=${encodeURIComponent(row.did_id)}&limit=50`,
        authHeaders,
      );
      setHistoryRows(Array.isArray(response.data?.checks) ? response.data.checks : []);
    } catch (error) {
      console.error('Failed to fetch check history', error);
      toast.error(error.response?.data?.detail || 'Failed to load check history.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openResultModal = async (row) => {
    // Prefer an already-open check; otherwise queue one first.
    try {
      const list = await axios.get(
        `${apiUrl}/api/v1/admin/spam/checks?did_number_id=${encodeURIComponent(row.did_id)}&limit=5`,
        authHeaders,
      );
      const open = (list.data?.checks || []).find((check) => !['done', 'error'].includes(check.state));
      if (open) {
        setResultCheck({ ...open, did_number: row.number });
        setResultDraft(emptyResultDraft);
        return;
      }
      const created = await handleRun([row.did_id]);
      if (created.length) {
        setResultCheck({ ...created[0], did_number: row.number });
        setResultDraft(emptyResultDraft);
      }
    } catch (error) {
      console.error('Failed to prepare result entry', error);
      toast.error(error.response?.data?.detail || 'Failed to prepare result entry.');
    }
  };

  const handleSubmitResult = async () => {
    if (!resultCheck) return;
    const checkId = resultCheck.check_ref || resultCheck.id;
    const payload = {
      app: resultDraft.app,
      overlay_present: !!resultDraft.overlay_present,
      caller_label: resultDraft.caller_label || null,
      is_spam: resultDraft.is_spam === 'unknown' ? null : resultDraft.is_spam === 'spam',
      report_count: resultDraft.report_count === '' ? null : Number(resultDraft.report_count),
      raw_text: resultDraft.raw_text || null,
      carrier_tag: resultDraft.carrier_tag || null,
      carrier_spam: resultDraft.carrier_spam === 'unknown' ? null : resultDraft.carrier_spam === 'spam',
    };
    try {
      setSubmittingResult(true);
      const response = await axios.post(
        `${apiUrl}/api/v1/admin/spam/checks/${checkId}/result`,
        payload,
        authHeaders,
      );
      if (response.data?.needs_retry) {
        toast.error(`Inconclusive (${response.data?.verdict || 'no signal'}) — retry queued (${response.data?.attempts || 1}/3).`);
      } else {
        toast.success(`Verdict recorded: ${response.data?.verdict || 'done'}.`);
      }
      setResultCheck(null);
      await fetchOverview();
    } catch (error) {
      console.error('Failed to submit spam result', error);
      toast.error(error.response?.data?.detail || 'Failed to submit result.');
    } finally {
      setSubmittingResult(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      const response = await axios.patch(
        `${apiUrl}/api/v1/admin/spam/settings`,
        {
          test_phone_number: null,
          target_numbers: (settingsDraft.target_numbers || []).map((entry) => ({
            number: entry.number,
            enabled: !!entry.enabled,
          })),
          daily_times: settingsDraft.daily_times || [],
          schedule_enabled: !!settingsDraft.schedule_enabled,
          auto_dial: !!settingsDraft.auto_dial,
          call_window_minutes: Number(settingsDraft.call_window_minutes) || 10,
        },
        authHeaders,
      );
      setSettings(response.data);
      toast.success('Spam detection settings saved.');
    } catch (error) {
      console.error('Failed to save spam settings', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddTime = () => {
    const value = newTime.trim();
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
      toast.error('Enter time as HH:MM (24h), e.g. 10:00.');
      return;
    }
    setSettingsDraft((current) => {
      const existing = Array.isArray(current.daily_times) ? current.daily_times : [];
      if (existing.includes(value)) {
        toast.error('That time is already added.');
        return current;
      }
      return { ...current, daily_times: [...existing, value].sort() };
    });
    setNewTime('');
  };

  const handleRemoveTime = (value) => {
    setSettingsDraft((current) => ({
      ...current,
      daily_times: (current.daily_times || []).filter((entry) => entry !== value),
    }));
  };

  // Add/remove/toggle used to only touch local draft state -- a deleted number
  // looked gone until someone remembered to also click "Save settings", and
  // reappeared on the next refresh because Mongo still had it. Persisting each
  // action immediately removes that footgun.
  const persistTargetNumbers = async (nextList) => {
    setSettingsDraft((current) => ({ ...current, target_numbers: nextList }));
    try {
      const response = await axios.patch(
        `${apiUrl}/api/v1/admin/spam/settings`,
        {
          target_numbers: nextList.map((entry) => ({ number: entry.number, enabled: !!entry.enabled })),
        },
        authHeaders,
      );
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to save target numbers', error);
      toast.error(error.response?.data?.detail || 'Failed to save — number list was not updated on the server.');
      await fetchOverview();
    }
  };

  const handleToggleSpamCheckEnabled = async (row, enabled) => {
    setNumbers((current) => current.map((entry) => (
      entry.did_id === row.did_id ? { ...entry, spam_check_enabled: enabled } : entry
    )));
    try {
      await axios.patch(
        `${apiUrl}/api/v1/admin/did-numbers/${row.did_id}`,
        { spam_check_enabled: enabled },
        authHeaders,
      );
      toast.success(enabled ? 'Number re-enabled for spam testing.' : 'Number excluded from spam testing.');
    } catch (error) {
      console.error('Failed to toggle spam-check for DID', error);
      toast.error(error.response?.data?.detail || 'Failed to update — reverting.');
      await fetchOverview();
    }
  };

  const handleAddNumber = () => {
    const value = newNumber.trim();
    if (!value) {
      toast.error('Enter a phone number first.');
      return;
    }
    const existing = Array.isArray(settingsDraft.target_numbers) ? settingsDraft.target_numbers : [];
    if (existing.some((entry) => entry.number === value)) {
      toast.error('That number is already added.');
      return;
    }
    persistTargetNumbers([...existing, { number: value, enabled: true }]);
    setNewNumber('');
  };

  const handleRemoveNumber = (value) => {
    const existing = Array.isArray(settingsDraft.target_numbers) ? settingsDraft.target_numbers : [];
    persistTargetNumbers(existing.filter((entry) => entry.number !== value));
  };

  const handleToggleNumber = (value, enabled) => {
    const existing = Array.isArray(settingsDraft.target_numbers) ? settingsDraft.target_numbers : [];
    persistTargetNumbers(existing.map((entry) => (entry.number === value ? { ...entry, enabled } : entry)));
  };

  const handleRegisterDevice = async () => {
    if (!deviceDraft.name.trim() || !deviceDraft.msisdn.trim()) {
      toast.error('Device name and number are required.');
      return;
    }
    try {
      setSavingDevice(true);
      const response = await axios.post(`${apiUrl}/api/v1/admin/spam/devices`, deviceDraft, authHeaders);
      setCreatedToken(response.data?.token || '');
      setDeviceDraft({ name: '', msisdn: '', network: '' });
      toast.success('Device registered. Copy the token into the app — it shows only once.');
      await fetchOverview();
    } catch (error) {
      console.error('Failed to register device', error);
      toast.error(error.response?.data?.detail || 'Failed to register device.');
    } finally {
      setSavingDevice(false);
    }
  };

  const handleRevokeDevice = async (device) => {
    try {
      await axios.delete(`${apiUrl}/api/v1/admin/spam/devices/${device.id}`, authHeaders);
      toast.success('Device revoked. Its token no longer works.');
      await fetchOverview();
    } catch (error) {
      console.error('Failed to revoke device', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke device.');
    }
  };

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(createdToken);
      toast.success('Token copied.');
    } catch (error) {
      console.error('Failed to copy token', error);
      toast.error('Copy failed — select the token text manually.');
    }
  };

  const trendData = useMemo(() => (
    [...historyRows]
      .reverse()
      .map((check) => ({
        at: formatIST(check.finished_at || check.created_at),
        reports: check.report_count ?? 0,
      }))
  ), [historyRows]);

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <main className="flex-1 flex flex-col min-w-0 p-6 lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6 w-full">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <ShieldAlert className="w-3.5 h-3.5" />
                Spam Detection
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Spam Number Dashboard</h1>
              <p className="mt-1 text-slate-500">
                Daily Truecaller screening across the DID pool. Report-only: numbers always stay in rotation.
              </p>
            </div>
          </div>

          <div>
            <div className="flex w-fit flex-wrap rounded-2xl border border-slate-200/60 bg-slate-100/80 p-1.5 shadow-inner">
              {VIEW_TABS.map((tab) => {
                const Icon = tab.icon;
                const active = tab.key === activeView;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveView(tab.key)}
                    className={`flex items-center gap-2 rounded-[14px] px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                      active
                        ? 'border border-slate-100 bg-white text-violet-700 shadow-md'
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <Icon size={15} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {VIEW_TABS.find((tab) => tab.key === activeView)?.hint}
            </p>
          </div>

          {activeView === 'dashboard' ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  { label: 'Active pool', value: stats ? `${stats.active} / ${stats.total}` : '—', sub: stats ? `${stats.quarantined} quarantined` : 'Loading…' },
                  { label: 'Flagged spam', value: stats ? `${stats.spam}` : '—', sub: stats ? `${spamRatePct}% of pool` : 'Loading…', alert: stats && stats.spam > 0 },
                  { label: 'Verified clean', value: stats ? `${stats.clean}` : '—', sub: stats ? `${stats.never_checked} never checked · ${stats.unverified} unverified` : 'Loading…' },
                  { label: 'Last scan', value: stats?.last_scan_at ? formatIST(stats.last_scan_at) : '—', sub: stats?.last_scan_at ? 'IST' : 'No scan yet' },
                ].map((card) => (
                  <div key={card.label} className="bg-white border rounded-2xl border-slate-200 shadow-sm p-5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</div>
                    <div className={`mt-2 text-3xl font-bold tracking-tight ${card.alert ? 'text-red-600' : 'text-slate-900'}`}>{card.value}</div>
                    <div className="mt-1 text-sm text-slate-500">{card.sub}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
                <div className="flex flex-col gap-3 p-4 border-b border-slate-100 sm:flex-row sm:items-center">
                  <div className="relative flex-1">
                    <Input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search numbers, providers, labels…" />
                  </div>
                  <Select value={verdictFilter} onValueChange={setVerdictFilter}>
                    <SelectTrigger className="h-9 w-full sm:w-44">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All numbers</SelectItem>
                      <SelectItem value="spam">Flagged spam</SelectItem>
                      <SelectItem value="clean">Verified clean</SelectItem>
                      <SelectItem value="unverified">Unverified</SelectItem>
                      <SelectItem value="unchecked">Never checked</SelectItem>
                      <SelectItem value="quarantined">Quarantined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1080px] text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                        <th className="px-4 py-3 font-semibold">Number</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Identified as</th>
                        <th className="px-4 py-3 font-semibold">Spam reports</th>
                        <th className="px-4 py-3 font-semibold">Network</th>
                        <th className="px-4 py-3 font-semibold">Truecaller</th>
                        <th className="px-4 py-3 font-semibold">Last checked</th>
                        <th className="px-4 py-3 font-semibold">Test this number</th>
                        <th className="px-4 py-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading DID pool…</td></tr>
                      ) : filteredNumbers.length === 0 ? (
                        <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No numbers match the current filter.</td></tr>
                      ) : filteredNumbers.map((row) => (
                        <tr key={row.did_id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{row.number || '—'}</div>
                            <div className="text-xs text-slate-400">{row.provider || 'No provider'} · {row.channel}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.quarantined ? 'bg-red-50 text-red-700 border border-red-200' : String(row.status || '').toLowerCase() === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                {row.quarantined ? 'Quarantined' : String(row.status || '').toLowerCase() === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${verdictBadge(row.spam_verdict)}`}>
                              {verdictLabel(row.spam_verdict)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-semibold ${typeof row.spam_report_count === 'number' && row.spam_report_count > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                              {typeof row.spam_report_count === 'number' ? row.spam_report_count : '—'}
                            </span>
                            {typeof row.spam_confidence === 'number' ? <div className="text-xs text-slate-400">{Math.round(row.spam_confidence * 100)}% conf · {row.spam_attempts || 0} att</div> : null}
                          </td>
                          <td className="px-4 py-3">
                            {row.spam_network_flagged ? (
                              <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                {row.spam_network || 'Carrier'} spam
                              </span>
                            ) : row.spam_network ? (
                              <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                                {row.spam_network}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {!row.spam_is_truecaller ? (
                              <span className="text-slate-400">Not shown</span>
                            ) : (
                              <div className="space-y-1">
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${row.spam_verdict === 'SPAM' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                                  {row.spam_verdict === 'SPAM' ? 'Spam' : 'Not spam'}
                                </span>
                                {row.spam_is_potential_fraud ? (
                                  <div>
                                    <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                                      Potential Fraud
                                    </span>
                                  </div>
                                ) : null}
                                {typeof row.spam_report_count === 'number' ? (
                                  <div className="text-xs text-slate-500">{row.spam_report_count} spam report{row.spam_report_count === 1 ? '' : 's'}</div>
                                ) : null}
                                {row.spam_label
                                  && row.spam_label.toLowerCase() !== 'potential fraud'
                                  && !row.spam_label.toLowerCase().includes('alert from')
                                  && !(row.spam_network && row.spam_label.toLowerCase().includes(row.spam_network.toLowerCase())) ? (
                                  <div className="text-xs text-slate-500 max-w-[200px] truncate" title={row.spam_label}>{row.spam_label}</div>
                                ) : null}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {row.spam_checked_at ? formatIST(row.spam_checked_at) : '—'}
                            {row.phone_name ? <div>{row.phone_name}{row.phone_network ? ` · ${row.phone_network}` : ''}</div> : null}
                          </td>
                          <td className="px-4 py-3">
                            <Switch
                              checked={row.spam_check_enabled !== false}
                              onCheckedChange={(checked) => handleToggleSpamCheckEnabled(row, checked)}
                              title={row.spam_check_enabled !== false ? 'Included in spam-check scans' : 'Excluded from spam-check scans'}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" title="Check now" onClick={() => openResultModal(row)}><PhoneCall className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" title="History" onClick={() => openHistory(row)}>History</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="bg-white border rounded-2xl border-slate-200 shadow-sm p-5 space-y-4">
                <h2 className="text-base font-semibold text-slate-900">Scan settings</h2>
                <div className="space-y-2">
                  <Label>Which numbers to call (check targets)</Label>
                  {(settingsDraft.target_numbers || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No numbers yet — checks will wait until at least one is added and enabled.</p>
                  ) : (settingsDraft.target_numbers || []).map((entry) => (
                    <div key={entry.number} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                      <span className={`flex-1 text-sm font-medium ${entry.enabled ? 'text-slate-900' : 'text-slate-400 line-through'}`}>{entry.number}</span>
                      <Switch
                        checked={!!entry.enabled}
                        onCheckedChange={(checked) => handleToggleNumber(entry.number, checked)}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveNumber(entry.number)}
                        title="Delete this number"
                        className="text-xs font-semibold text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={newNumber}
                      onChange={(event) => setNewNumber(event.target.value)}
                      placeholder="+91 …"
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={handleAddNumber}>Add</Button>
                  </div>
                  <p className="text-xs text-slate-400">The first enabled number is rung from each DID. Toggle off to skip without deleting.</p>
                </div>
                <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-4">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Daily schedule</div>
                    <p className="mt-1 text-xs text-slate-500">Queue a full-pool scan at these IST times, every day. Several times allowed.</p>
                  </div>
                  <Switch
                    checked={!!settingsDraft.schedule_enabled}
                    onCheckedChange={(checked) => setSettingsDraft((current) => ({ ...current, schedule_enabled: checked }))}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {(settingsDraft.daily_times || []).length === 0 ? (
                      <span className="text-xs text-slate-400">No times set — scans run once every {settings?.check_interval_hours || 24}h instead. Add times below for multiple daily runs.</span>
                    ) : (settingsDraft.daily_times || []).map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => handleRemoveTime(slot)}
                        title="Remove this time"
                        className="rounded-full bg-violet-50 border border-violet-200 px-3 py-1 text-xs font-semibold text-violet-700 hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                      >
                        {slot} ✕
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="time"
                      value={newTime}
                      onChange={(event) => setNewTime(event.target.value)}
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={handleAddTime}>Add</Button>
                  </div>
                  {settings?.last_scheduled_run_at ? (
                    <p className="text-xs text-slate-400">Last auto run: {formatIST(settings.last_scheduled_run_at)} IST</p>
                  ) : null}
                </div>
                <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-4">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Auto-dial at schedule</div>
                    <p className="mt-1 text-xs text-slate-500">Core itself rings each DID when a slot fires. Off = queue only, dial by hand.</p>
                  </div>
                  <Switch
                    checked={!!settingsDraft.auto_dial}
                    onCheckedChange={(checked) => setSettingsDraft((current) => ({ ...current, auto_dial: checked }))}
                  />
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="font-medium text-slate-900 text-sm">Call window (minutes)</div>
                  <p className="mt-1 text-xs text-slate-500">
                    Stop dialing after this many minutes once a slot fires — whatever's left over waits for the
                    next scheduled run instead of continuing. Example: window = 10 → calls for up to 10 minutes,
                    then picks up the rest next time.
                  </p>
                  <Input
                    type="number"
                    min="1"
                    max="120"
                    value={settingsDraft.call_window_minutes ?? 10}
                    onChange={(event) => setSettingsDraft((current) => ({
                      ...current,
                      call_window_minutes: event.target.value === '' ? '' : Number(event.target.value),
                    }))}
                    className="mt-2 w-28"
                  />
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800">
                  {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save settings
                </Button>
              </div>

              <div className="bg-white border rounded-2xl border-slate-200 shadow-sm p-5 space-y-4">
                <h2 className="text-base font-semibold text-slate-900">App devices</h2>
                <p className="text-xs text-slate-500">Phones running the detector app. Register one to get its token, type it into the app once — the token shows only here, only once.</p>
                {createdToken ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-xs font-semibold text-emerald-800">New device token (copy now — never shown again)</div>
                    <div className="mt-1 break-all font-mono text-xs text-emerald-900">{createdToken}</div>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="outline" onClick={handleCopyToken}>Copy</Button>
                      <Button size="sm" variant="ghost" onClick={() => setCreatedToken('')}>Hide</Button>
                    </div>
                  </div>
                ) : null}
                {devices.length === 0 ? (
                  <p className="text-sm text-slate-500">No app devices yet.</p>
                ) : devices.map((device) => (
                  <div key={device.id} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-slate-900 text-sm">{device.name}</div>
                      <Button size="sm" variant="ghost" onClick={() => handleRevokeDevice(device)} className="text-red-600">Revoke</Button>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{device.msisdn}{device.network ? ` · ${device.network}` : ''}</div>
                    <div className="text-xs text-slate-400">Last report: {device.last_seen_at ? formatIST(device.last_seen_at) : 'never'}</div>
                  </div>
                ))}
                <div className="space-y-2 border-t border-slate-100 pt-4">
                  <Label>Register app device</Label>
                  <Input value={deviceDraft.name} onChange={(event) => setDeviceDraft((c) => ({ ...c, name: event.target.value }))} placeholder="Name (e.g. Jio Phone)" />
                  <Input value={deviceDraft.msisdn} onChange={(event) => setDeviceDraft((c) => ({ ...c, msisdn: event.target.value }))} placeholder="MSISDN (+91 …)" />
                  <Input value={deviceDraft.network} onChange={(event) => setDeviceDraft((c) => ({ ...c, network: event.target.value }))} placeholder="Network (Jio / Airtel / Vi)" />
                  <Button onClick={handleRegisterDevice} disabled={savingDevice} variant="outline" className="w-full">
                    {savingDevice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Register + show token
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {historyNumber ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setHistoryNumber(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Check history — {historyNumber.number}</h3>
                <p className="text-sm text-slate-500">Latest {historyRows.length} runs with per-attempt frames.</p>
              </div>
              <Button variant="ghost" onClick={() => setHistoryNumber(null)}>Close</Button>
            </div>
            {trendData.length > 1 ? (
              <div className="mt-4 h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="at" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="reports" stroke="#dc2626" strokeWidth={2} dot={false} name="Spam reports" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : null}
            <div className="mt-4 space-y-3">
              {loadingHistory ? (
                <div className="py-8 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading history…</div>
              ) : historyRows.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">No checks recorded for this number yet.</div>
              ) : historyRows.map((check) => (
                <div key={check.id} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${verdictBadge(check.verdict)}`}>{verdictLabel(check.verdict)}</span>
                    <span className="text-slate-500 text-xs">{formatIST(check.finished_at || check.created_at)}</span>
                    <span className="text-slate-400 text-xs">{check.attempts} attempt{(check.attempts || 0) === 1 ? '' : 's'}{check.phone_name ? ` · ${check.phone_name}` : ''}</span>
                  </div>
                  {(check.frames || []).map((frame) => (
                    <div key={frame.attempt} className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <span className="font-semibold">Attempt {frame.attempt}:</span> {frame.verdict}
                      {frame.parsed?.caller_label ? ` — “${frame.parsed.caller_label}”` : ''}
                      {typeof frame.parsed?.report_count === 'number' ? ` — ${frame.parsed.report_count} spam report${frame.parsed.report_count === 1 ? '' : 's'}` : ''}
                      {frame.parsed?.clean_text ? (
                        <div className="mt-1 text-slate-500 break-words">{frame.parsed.clean_text}</div>
                      ) : (
                        <div className="mt-1 text-slate-400 italic">No caller-ID verdict was captured on this attempt (only system/notification text).</div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {resultCheck ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setResultCheck(null)}>
          <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900">Submit screen result — {resultCheck.did_number}</h3>
            <p className="mt-1 text-sm text-slate-500">
              Ring the test phone from this DID, read what Truecaller shows on the incoming-call screen, and enter it below.
            </p>
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Caller-ID app</Label>
                <Select value={resultDraft.app} onValueChange={(value) => setResultDraft((c) => ({ ...c, app: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truecaller">Truecaller</SelectItem>
                    <SelectItem value="google_phone">Google Phone</SelectItem>
                    <SelectItem value="hiya">Hiya</SelectItem>
                    <SelectItem value="airtel_spam">Airtel anti-spam</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="none">No overlay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3">
                <div>
                  <Label>Overlay visible</Label>
                  <p className="text-xs text-slate-500">Did a caller-ID popup appear over the dialer?</p>
                </div>
                <Switch checked={!!resultDraft.overlay_present} onCheckedChange={(checked) => setResultDraft((c) => ({ ...c, overlay_present: checked }))} />
              </div>
              <div className="space-y-2">
                <Label>Shown as (label / name)</Label>
                <Input value={resultDraft.caller_label} onChange={(event) => setResultDraft((c) => ({ ...c, caller_label: event.target.value }))} placeholder="e.g. Fraud, Spammer, business name…" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Warning shown?</Label>
                  <Select value={resultDraft.is_spam} onValueChange={(value) => setResultDraft((c) => ({ ...c, is_spam: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spam">Spam / fraud</SelectItem>
                      <SelectItem value="clean">Clean, identified</SelectItem>
                      <SelectItem value="unknown">Unknown / unreadable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Spam reports</Label>
                  <Input type="number" min="0" value={resultDraft.report_count} onChange={(event) => setResultDraft((c) => ({ ...c, report_count: event.target.value }))} placeholder="146" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Network tag on screen</Label>
                  <Input value={resultDraft.carrier_tag || ''} onChange={(event) => setResultDraft((c) => ({ ...c, carrier_tag: event.target.value }))} placeholder="e.g. Vi, Jio, Airtel" />
                </div>
                <div className="space-y-2">
                  <Label>Network flagged spam?</Label>
                  <Select value={resultDraft.carrier_spam || 'unknown'} onValueChange={(value) => setResultDraft((c) => ({ ...c, carrier_spam: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spam">Yes — Jio/Airtel/Vi spam tag</SelectItem>
                      <SelectItem value="clean">No — just the name, no tag</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>All readable text on screen</Label>
                <Textarea value={resultDraft.raw_text} onChange={(event) => setResultDraft((c) => ({ ...c, raw_text: event.target.value }))} className="min-h-[90px]" placeholder="Paste everything the overlay shows, including Hindi warnings…" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setResultCheck(null)}>Cancel</Button>
                <Button onClick={handleSubmitResult} disabled={submittingResult} className="text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800">
                  {submittingResult ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Submit result
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
